// src/pages/dashboard/components/DateRangePresetSelect.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './DateRangePresetSelect.module.css';

const PRESETS = [
  { key: 'today', labelKey: 'dashboard.today' },
  { key: 'yesterday', labelKey: 'dashboard.yesterday' },
  { key: 'thisWeek', labelKey: 'dashboard.thisWeekLabel' },
  { key: 'lastWeek', labelKey: 'dashboard.lastWeek' },
  { key: 'thisMonth', labelKey: 'dashboard.thisMonthLabel' },
  { key: 'lastMonth', labelKey: 'dashboard.lastMonth' },
];

const formatDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const getDateRangeByPreset = (preset) => {
  const today = new Date();
  const weekStart = addDays(today, -today.getDay());

  switch (preset) {
    case 'today':
      return { from: formatDate(today), to: formatDate(today) };
    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { from: formatDate(yesterday), to: formatDate(yesterday) };
    }
    case 'thisWeek':
      return { from: formatDate(weekStart), to: formatDate(today) };
    case 'lastWeek':
      return { from: formatDate(addDays(weekStart, -7)), to: formatDate(addDays(weekStart, -1)) };
    case 'lastMonth':
      return {
        from: formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: formatDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'thisMonth':
    default:
      return { from: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: formatDate(today) };
  }
};

const DateRangePresetSelect = ({ value, onChange }) => {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = PRESETS.find(p => p.key === value);

  return (
    <div className={styles.dropdownWrapper} ref={wrapperRef}>
      <button type="button" className={styles.filterButton} onClick={() => setOpen(!open)}>
        <Calendar size={14} />
        <span>{selected ? t(selected.labelKey) : t('dashboard.customRange')}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className={styles.dropdownMenu}>
          {PRESETS.map(preset => (
            <div
              key={preset.key}
              className={`${styles.dropdownItem} ${value === preset.key ? styles.active : ''}`}
              onClick={() => {
                onChange(preset.key);
                setOpen(false);
              }}
            >
              {t(preset.labelKey)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateRangePresetSelect;
